import { cn } from "@/lib/utils";

interface Benefit {
  title: string;
  description: string;
  icon?: string;
}

interface BenefitsListProps {
  benefits: Benefit[];
  variant?: 'default' | 'grid' | 'compact';
  columns?: 1 | 2 | 3;
  iconStyle?: 'check' | 'custom' | 'none';
  iconColor?: string;
  iconBgColor?: string;
  className?: string;
}

export default function BenefitsList({
  benefits,
  variant = 'default',
  columns = 1,
  iconStyle = 'check',
  iconColor = 'text-green-600',
  iconBgColor = 'bg-green-100',
  className = ''
}: BenefitsListProps) {
  const getIcon = (benefit: Benefit) => {
    if (iconStyle === 'none') return null;
    if (iconStyle === 'custom' && benefit.icon) return benefit.icon;
    return 'fas fa-check';
  };

  const renderBenefit = (benefit: Benefit, index: number) => (
    <div key={index} className="flex items-start">
      {iconStyle !== 'none' && (
        <div className="flex-shrink-0 mt-1">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center",
            iconBgColor
          )}>
            <i className={cn(getIcon(benefit), iconColor)}></i>
          </div>
        </div>
      )}
      <div className={cn("ml-4", iconStyle === 'none' && "ml-0")}>
        <h4 className="text-lg font-semibold mb-1">{benefit.title}</h4>
        <p className="text-neutral-muted">{benefit.description}</p>
      </div>
    </div>
  );

  if (variant === 'grid') {
    const gridCols = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    };

    return (
      <div className={cn("grid gap-6", gridCols[columns], className)}>
        {benefits.map((benefit, index) => renderBenefit(benefit, index))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn("space-y-3", className)}>
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-start">
            {iconStyle !== 'none' && (
              <div className="flex-shrink-0 mt-1">
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center",
                  iconBgColor
                )}>
                  <i className={cn(getIcon(benefit), iconColor, "text-xs")}></i>
                </div>
              </div>
            )}
            <div className={cn("ml-3", iconStyle === 'none' && "ml-0")}>
              <h5 className="font-semibold mb-1 text-sm">{benefit.title}</h5>
              <p className="text-neutral-muted text-sm">{benefit.description}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default variant - similar to ServiceBenefitItem but as a list
  return (
    <div className={cn("space-y-6", className)}>
      {benefits.map((benefit, index) => renderBenefit(benefit, index))}
    </div>
  );
}
